import React, { useState, HTMLProps } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "./LoginDebtId.scss";
import loginService from "../../services/LoginService";
import debtNumberImage from "../../../resources/images/Anschreiben_Forderungsnummer.svg";
import debtNumberImageCH from "../../../resources/images/Anschreiben_Forderungsnummer_CH.svg";
import { SPOTInput } from "spot-component-library/src/components/form/spotInput/SPOTInput";
import { useTenant } from "../../contexts/TenantContext";
import { Tenant } from "../../services/TenantService/TenantService";
import { Dialog } from "spot-component-library/src/components/basic/dialog/Dialog";
import { SPOTButton } from "spot-component-library/src/components/SPOTButton";
import { SPOTNotification } from "spot-component-library/src/components/notification/SPOTNotification";

export interface LoginProps {
    buttonText?: string | null;
    buttonPosition?: string | null;
}

export const LoginDebtId = (props: LoginProps) => {
    const tenant = useTenant();

    const { t } = useTranslation();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [debtNumber, setDebtNumber] = useState<string>("");
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [isPageLoaded, setIsPageLoaded] = React.useState(false);
    const [debtNumberRegExp, setDebtNumberRegExp] = React.useState<RegExp>(/.*/);
    const [debtNumberMaxLength, setDebtNumberMaxLength] = React.useState<number>(11);

    enum SubmitType {
        ON_SUBMIT = "ON_SUBMIT",
        ON_BLUR = "ON_BLUR",
    }
    const currentSubmitType = props.buttonText ? SubmitType.ON_SUBMIT : SubmitType.ON_BLUR;

    const {
        register,
        trigger,
        setError,
        formState: { errors, isValid },
    } = useForm();

    React.useEffect(() => {
        if (tenant) {
            initData(tenant)
                .then(() => setIsPageLoaded(true))
                .finally(() => setIsPageLoaded(true));
        }
    }, [tenant]);

    const initData = async (tenant: Tenant) => {
        if (tenant && tenant.validationPattern) {
            const [debtNumberRegExp, debtNumberMaxLength] = tenant.validationPattern;
            setDebtNumberRegExp(debtNumberRegExp);
            setDebtNumberMaxLength(debtNumberMaxLength);
        }

        loginService.getDebtNumber().then((response) => {
            if (response) {
                setDebtNumber(response);
            }
        });
    };

    const handleDebtNumberOnChange = (e: any) => {
        const value = e.target.value.toUpperCase();
        setDebtNumber(value);
        e.target.value = value;
    };

    const loginWithDebtNumber = async (type: SubmitType) => {
        // SPOT-4387 Workaround - delay the error message which move the link down outside the click event
        await new Promise((resolve) => setTimeout(resolve, 500));

        const result = await trigger("debtNumber");
        setErrorMessage(undefined);

        if (type !== currentSubmitType) {
            return;
        }

        if (result) {
            setLoadingButton(true);

            try {
                await loginService.loginWithDebtNumber(debtNumber);

                const successEvent = new CustomEvent("loginSuccessful", {
                    detail: {
                        debtId: debtNumber,
                    },
                    bubbles: true,
                    cancelable: true,
                    composed: false,
                });
                window.dispatchEvent(successEvent);
            } catch (e) {
                const error = e as Error;
                switch (error.message) {
                    case "401":
                        setError("debtNumber", { message: t("ERROR.DEBT_NUMBER.MESSAGE_401") });
                        break;
                    case "403":
                        setError("debtNumber", { message: t("ERROR.DEBT_NUMBER.DEFAULT") });
                        break;
                    case "429":
                        setError("debtNumber", {});
                        setErrorMessage(t("ERROR.MESSAGE_429"));
                        break;
                    default: {
                        setError("debtNumber", {});
                        setErrorMessage(t("ERROR.SERVER_MESSAGE_DEFAULT"));
                        break;
                    }
                }
            }
            setLoadingButton(false);
        }
    };

    return (
        <div className={"login-wrapper" + props.buttonPosition}>
            <div>
                <SPOTInput
                    label={t("LOGIN.DebtNumber")}
                    id="debtNumber"
                    name="debtNumber"
                    onChange={handleDebtNumberOnChange}
                    onBlur={() => loginWithDebtNumber(SubmitType.ON_BLUR)}
                    placeholder={t("LOGIN.DebtNumberDescription")}
                    maxLength={debtNumberMaxLength}
                    register={register as any}
                    required={t("VALIDATION.DEBT_NUMBER_INVALID") as string}
                    pattern={{
                        value: debtNumberRegExp,
                        message: t("VALIDATION.DEBT_NUMBER_INVALID"),
                    }}
                    errors={errors}
                    defaultValue={debtNumber}
                    autofocus={true}
                />
                {errorMessage && <SPOTNotification text={errorMessage} type={"warning"} active={true} />}
                <Dialog
                    closeIcon={true}
                    triggerText={t("LOGIN.DebtNumberHelpText")}
                    triggerIcon="icon-questions-V2"
                    dialogStyle={{ maxWidth: "600px", width: "85%" }}
                    header={t("LOGIN.DebtNumberHelpText")}>
                    {t("LOGIN.DEBT_NUMBER_HELP_HINT")}
                    {tenant?.name && <img src={tenant.name === "ch-eos" ? debtNumberImageCH : debtNumberImage} alt={""} className={"debt-number-image"} />}
                </Dialog>
            </div>

            {currentSubmitType === SubmitType.ON_SUBMIT && (
                <div className="claim-login-button">
                    <SPOTButton
                        id="debtIdLoginBtn"
                        type="primary"
                        onClick={() => loginWithDebtNumber(SubmitType.ON_SUBMIT)}
                        isLoading={loadingButton}
                        label={props.buttonText as string}
                    />
                </div>
            )}
        </div>
    );
};
